import { useEffect, useMemo, useState, type ChangeEvent, type FormEvent } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { CONTACT_ENDPOINT_NON_JSON_ERROR, sendContactLead } from '../../lib/contactApi';

interface ContactModalProps {
  isOpen: boolean;
  onClose: () => void;
  presetService?: string | null;
  showBudget?: boolean;
}

type QuickLeadData = {
  fullName: string;
  email: string;
  budget: string;
  message: string;
};

const WHATSAPP_NUMBER = '421905929167';

const initialFormData: QuickLeadData = {
  fullName: '',
  email: '',
  budget: '',
  message: '',
};

export default function ContactModal({
  isOpen,
  onClose,
  presetService = null,
  showBudget = true,
}: ContactModalProps) {
  const { t } = useTranslation();
  const [formData, setFormData] = useState<QuickLeadData>(initialFormData);
  const [submitStatus, setSubmitStatus] = useState<
    'idle' | 'email-loading' | 'email-success' | 'email-error' | 'whatsapp-loading'
  >('idle');
  const [feedbackMessage, setFeedbackMessage] = useState('');

  useEffect(() => {
    if (!isOpen) {
      setFormData(initialFormData);
      setSubmitStatus('idle');
      setFeedbackMessage('');
    }
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) {
      return;
    }

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onClose();
      }
    };

    window.addEventListener('keydown', handleKeyDown);

    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [isOpen, onClose]);

  const isServiceFlow = showBudget || Boolean(presetService);
  const budgetOptions = t('modal.budgetOptions', { returnObjects: true }) as string[];

  const whatsappHref = useMemo(() => {
    const lines = [
      t('modal.whatsappIntro'),
      '',
      t('modal.whatsappInterest'),
      presetService ? `${t('modal.whatsappArea')}: ${presetService}` : null,
      formData.fullName ? `${t('modal.whatsappName')}: ${formData.fullName}` : null,
      formData.email ? `${t('modal.whatsappEmail')}: ${formData.email}` : null,
      showBudget && formData.budget ? `${t('modal.whatsappBudget')}: ${formData.budget}` : null,
      formData.message ? `${t('modal.whatsappMessage')}: ${formData.message}` : null,
    ].filter(Boolean);

    return `https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent(lines.join('\n'))}`;
  }, [
    formData.budget,
    formData.email,
    formData.fullName,
    formData.message,
    presetService,
    showBudget,
    t,
  ]);

  const handleChange = (
    event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
  ) => {
    const { name, value } = event.target;
    setFormData((current) => ({ ...current, [name]: value }));
    if (submitStatus !== 'idle') {
      setSubmitStatus('idle');
      setFeedbackMessage('');
    }
  };

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setSubmitStatus('email-loading');
    setFeedbackMessage('');

    try {
      const result = await sendContactLead({
        source: 'quick-modal',
        fullName: formData.fullName,
        email: formData.email,
        budget: showBudget ? formData.budget : undefined,
        message: formData.message,
        service: presetService,
      });

      if (!result.ok) {
        setSubmitStatus('email-error');
        setFeedbackMessage(t('modal.fallbackError'));
        return;
      }

      setSubmitStatus('email-success');
      setFeedbackMessage(t('modal.successMessage'));
      window.setTimeout(() => {
        onClose();
      }, 1300);
    } catch (error) {
      setSubmitStatus('email-error');
      setFeedbackMessage(
        error instanceof Error && error.message === CONTACT_ENDPOINT_NON_JSON_ERROR
          ? t('modal.endpointError')
          : t('modal.fallbackError')
      );
    }
  };

  const handleWhatsAppClick = () => {
    setSubmitStatus('whatsapp-loading');
    window.open(whatsappHref, '_blank', 'noopener,noreferrer');
    window.setTimeout(() => {
      setSubmitStatus('idle');
      onClose();
    }, 120);
  };

  return (
    <AnimatePresence>
      {isOpen ? (
        <div className="fixed inset-0 z-[120] overflow-y-auto p-4 sm:p-6">
          <motion.button
            type="button"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.22 }}
            className="absolute inset-0 bg-black/72 backdrop-blur-md"
            onClick={onClose}
            aria-label={t('modal.close')}
          />

          <div className="relative flex min-h-full items-start justify-center lg:items-center">
          <motion.div
            initial={{ opacity: 0, y: 26, scale: 0.97 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 22, scale: 0.98 }}
            transition={{ duration: 0.28, ease: [0.22, 1, 0.36, 1] }}
            className="relative z-10 flex max-h-[calc(100vh-2rem)] w-full max-w-5xl flex-col overflow-hidden rounded-[34px] border border-white/10 bg-[linear-gradient(180deg,rgba(26,26,26,0.95)_0%,rgba(26,26,26,0.88)_100%)] shadow-[0_30px_90px_rgba(0,0,0,0.42)] backdrop-blur-2xl sm:max-h-[calc(100vh-3rem)]"
          >
            <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_16%_14%,rgba(163,230,53,0.12),transparent_24%),radial-gradient(circle_at_86%_14%,rgba(64,196,255,0.12),transparent_24%)]" />

            <div className="relative z-10 grid gap-0 overflow-y-auto lg:grid-cols-[minmax(0,0.96fr)_minmax(380px,1.04fr)]">
              <div className="border-b border-white/10 p-7 sm:p-9 lg:border-b-0 lg:border-r lg:p-10">
                <div className="inline-flex items-center rounded-full border border-white/10 bg-white/[0.05] px-4 py-2 text-sm font-semibold text-brand-green">
                  <span className="mr-2 inline-flex items-center">
                    <i className="ri-chat-1-line text-base" />
                  </span>
                  {isServiceFlow ? t('modal.quickRequest') : t('modal.simpleContact')}
                </div>

                <h2 className="mt-5 text-3xl font-caprasimo leading-tight text-white sm:text-4xl lg:text-5xl">
                  {isServiceFlow ? t('modal.quickTitle') : t('modal.simpleTitle')}
                </h2>
                <p className="mt-4 max-w-lg text-base leading-8 text-white/66 sm:text-lg">
                  {isServiceFlow
                    ? t('modal.quickDescription')
                    : t('modal.simpleDescription')}
                </p>

                {presetService ? (
                  <div className="mt-7 rounded-[24px] border border-white/10 bg-white/[0.04] p-5">
                    <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-brand-green">
                      {t('modal.selectedService')}
                    </p>
                    <p className="mt-2 text-xl font-caprasimo text-white">{presetService}</p>
                  </div>
                ) : null}

                <div className="mt-7 rounded-[24px] border border-white/10 bg-black/18 p-5 text-sm leading-7 text-white/62 sm:text-base">
                  {isServiceFlow ? (
                    <>
                      <p>{t('modal.quickHelpPrimary')}</p>
                      <p className="mt-3">{t('modal.quickHelpSecondary')}</p>
                    </>
                  ) : (
                    <>
                      <p>{t('modal.simpleHelpPrimary')}</p>
                      <p className="mt-3">{t('modal.simpleHelpSecondary')}</p>
                    </>
                  )}
                </div>
              </div>

              <div className="p-7 sm:p-9 lg:p-10">
                <div className="mb-7 flex items-start justify-between gap-4">
                  <div>
                    <h3 className="text-3xl font-caprasimo text-white">
                      {isServiceFlow ? t('modal.formTitleQuick') : t('modal.formTitleSimple')}
                    </h3>
                    <p className="mt-2 text-sm leading-6 text-white/60 sm:text-base">
                      {isServiceFlow
                        ? t('modal.formDescriptionQuick')
                        : t('modal.formDescriptionSimple')}
                    </p>
                  </div>

                  <button
                    type="button"
                    onClick={onClose}
                    className="inline-flex h-10 w-10 items-center justify-center rounded-full border border-white/10 bg-white/[0.04] text-white/72 transition-colors hover:bg-white/[0.08] hover:text-white"
                    aria-label={t('modal.close')}
                  >
                    <i className="ri-close-line text-lg" />
                  </button>
                </div>

                <form onSubmit={handleSubmit} className="space-y-5">
                  <div className="grid gap-4 sm:grid-cols-2">
                    <input
                      type="text"
                      name="fullName"
                      value={formData.fullName}
                      onChange={handleChange}
                      required
                      className="w-full rounded-2xl border border-white/10 bg-black/18 px-4 py-4 text-sm text-white outline-none transition-all placeholder:text-white/30 focus:border-brand-green/45 focus:bg-black/24"
                      placeholder={t('modal.placeholders.fullName')}
                    />
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      required
                      className="w-full rounded-2xl border border-white/10 bg-black/18 px-4 py-4 text-sm text-white outline-none transition-all placeholder:text-white/30 focus:border-brand-green/45 focus:bg-black/24"
                      placeholder={t('modal.placeholders.email')}
                    />
                  </div>

                  {showBudget ? (
                    <select
                      name="budget"
                      value={formData.budget}
                      onChange={handleChange}
                      required
                      className="w-full rounded-2xl border border-white/10 bg-black/18 px-4 py-4 text-sm text-white outline-none transition-all focus:border-brand-green/45 focus:bg-black/24"
                    >
                      <option value="" className="bg-[#121212] text-white/70">
                        {t('modal.placeholders.budget')}
                      </option>
                      {budgetOptions.map((option) => (
                        <option key={option} value={option} className="bg-[#121212] text-white">
                          {option}
                        </option>
                      ))}
                    </select>
                  ) : null}

                  <textarea
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    required
                    rows={7}
                    className="w-full resize-none rounded-2xl border border-white/10 bg-black/18 px-4 py-4 text-sm text-white outline-none transition-all placeholder:text-white/30 focus:border-brand-green/45 focus:bg-black/24"
                    placeholder={t('modal.placeholders.message')}
                  />

                  <div className="space-y-3 pt-2">
                    <button
                      type="submit"
                      disabled={submitStatus === 'email-loading'}
                      className="inline-flex min-h-[68px] w-full items-center justify-between rounded-[20px] border border-white/10 bg-white/[0.05] px-5 py-4 text-left text-[1rem] font-semibold text-white transition-all hover:-translate-y-0.5 hover:bg-white/[0.08]"
                    >
                      <span className="pr-4">
                        <span className="block">
                          {submitStatus === 'email-loading'
                            ? t('modal.sendingMessage')
                            : t('modal.sendMessage')}
                        </span>
                        <span className="mt-1 block text-sm font-normal text-white/56">
                          {t('modal.sendMessageHint')}
                        </span>
                      </span>
                      <span className="flex h-10 w-10 items-center justify-center rounded-full bg-white/[0.08] text-white/84">
                        <i
                          className={`text-base ${submitStatus === 'email-loading' ? 'ri-loader-4-line animate-spin' : 'ri-mail-send-line'}`}
                        />
                      </span>
                    </button>

                    <button
                      type="button"
                      onClick={handleWhatsAppClick}
                      disabled={submitStatus === 'email-loading' || submitStatus === 'whatsapp-loading'}
                      className="inline-flex min-h-[68px] w-full items-center justify-between rounded-[20px] border border-white/10 bg-gradient-to-r from-brand-green via-[#b4ec4b] to-[#c8f06d] px-5 py-4 text-left text-[1rem] font-black text-[#0D0D0D] shadow-[0_12px_24px_rgba(163,230,53,0.22)] transition-all hover:-translate-y-0.5 hover:shadow-[0_16px_30px_rgba(163,230,53,0.28)]"
                    >
                      <span className="pr-4">
                        <span className="block">
                          {submitStatus === 'whatsapp-loading'
                            ? t('modal.openWhatsapp')
                            : t('modal.sendWhatsapp')}
                        </span>
                        <span className="mt-1 block text-sm font-medium text-[#0D0D0D]/72">
                          {t('modal.whatsappHint')}
                        </span>
                      </span>
                      <span className="flex h-9 w-9 items-center justify-center rounded-full bg-black/10 text-[#0D0D0D] shadow-[inset_0_1px_0_rgba(255,255,255,0.22)]">
                        <i className="ri-whatsapp-line text-base" />
                      </span>
                    </button>
                  </div>

                  {submitStatus === 'email-success' ? (
                    <div className="rounded-2xl border border-brand-green/18 bg-brand-green/[0.08] px-4 py-3 text-sm text-white/82">
                      {feedbackMessage}
                    </div>
                  ) : null}
                  {submitStatus === 'email-error' ? (
                    <div className="rounded-2xl border border-[#ff8f8f]/18 bg-[#ff8f8f]/[0.08] px-4 py-3 text-sm text-white/82">
                      {feedbackMessage}
                    </div>
                  ) : null}
                </form>
              </div>
            </div>
          </motion.div>
          </div>
        </div>
      ) : null}
    </AnimatePresence>
  );
}
